import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { ObjectType, TextState } from '~/static/enums/object-type'
import { TextAlign } from '~/static/enums/text-align'
import { EditorObject, LayoutContext, ObjectState } from '@/utils/editor/object.class'
import { FillType } from '~/static/enums/components/color-input'
import { setTextboxSideControls } from '~/utils/editor/helpers/ControlsHelper'
import { getRoundPath, getRoundRadius, getTextboxSize } from '~/utils/editor/helpers/RoundTextHelper'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/text'
import { DESKTOP_WIDTH } from '~/static/window-sizing'
import { assignNewColorAndAlpha, makeFabricGradient } from '~/utils/ColorHelper'
import { getRegisterFamilyName, loadFonts } from '~/utils/FontsHelper'
import { makeShadow } from '~/utils/editor/helpers/Helpers'
import { Action } from '~/static/types/editor'

const DefaultFontSize = 23.2
const DefaultLineSpacing = 24.36

export class Text extends EditorObject {
  areaWidth: number = 60
  areaHeight: number = 26
  colorType: FillType = FillType.Solid
  color: FabricGradientOptions | string = 'rgba(0,0,0,1)'
  opacity: number = 100
  underline: boolean = false
  linethrough: boolean = false
  width: number = 400
  italic: boolean = false
  fontFamily: string = 'Roboto'
  align: TextAlign = TextAlign.Left
  lineSpacing: number = DefaultLineSpacing
  fontSize: number = DefaultFontSize
  shadow: boolean = false
  shadowColor: string = 'rgba(0,0,0,1)'
  shadowOpacity: number = 100
  shadowX: number = 0
  shadowY: number = 4
  shadowBlur: number = 4
  round: number = 0
  text!: string

  textObject!: fabricTypes.Textbox

  dirtyFont: boolean = true
  dirtyPosition: boolean = true
  fallbackFont: boolean = false

  constructor(props: ActualVersion) {
    super(ObjectType.Text)
    this.assignProperties(props)
  }

  load(): Promise<void> {
    this.textObject = new fabric.Textbox(this.text, { width: this.areaWidth, objectCaching: false })
    this.setObject(new fabric.Group([ this.textObject, new fabric.Rect() ], { objectCaching: false }))
    return Promise.resolve()
  }

  applyInitialLayout(context: LayoutContext): void {
    // Use lastFontFamily from context if available, otherwise keep default
    if (context.lastFontFamily) {
      this.fontFamily = context.lastFontFamily
    }
    
    const textWidth = new fabric.FabricText(this.text, {
      width: 0,
      height: 0,
      lineHeight: DefaultLineSpacing / DefaultFontSize,
      fontSize: DefaultFontSize,
      fontFamily: getRegisterFamilyName(this.fontFamily, this.width, this.italic),
      fontWeight: 400,
    })
    textWidth.dirty = true
    this.areaWidth = Math.max(textWidth.width ?? 0, this.areaWidth)

    if (this.areaWidth / context.zoom > context.width) {
      this.areaWidth = context.width * context.zoom
    }
    this.areaWidth = Math.round(this.areaWidth / context.zoom)
    this.areaHeight = Math.round(this.areaHeight / context.zoom)
    this.fontSize = Math.round(this.fontSize / context.zoom)
    this.lineSpacing = Math.round(this.lineSpacing / context.zoom)
  }

  applyDefaultName(): void {}

  async set(props: ActualVersion): Promise<void> {
    this.assignProperties(props)
    return this.applyProps()
  }

  protected assignProperties(props: ActualVersion): void {
    this.dirtyFont = props.italic !== undefined ||
      props.width !== undefined ||
      props.fontFamily !== undefined ||
      this.dirtyFont

    this.dirtyPosition = this.round !== 0 ||
      'round' in props ||
      'text' in props ||
      'areaWidth' in props ||
      'areaHeight' in props ||
      props.lineSpacing !== undefined ||
      props.fontSize !== undefined ||
      props.fontFamily !== undefined ||
      this.dirtyPosition

    super.assignProperties(props)

    const color = assignNewColorAndAlpha(this.colorType, this.color, this.opacity, props.color, props.opacity)
    this.color = color.color
    this.opacity = color.opacity

    const shadowColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.shadowColor,
      this.shadowOpacity,
      props.shadowColor,
      props.shadowOpacity,
    )
    this.shadowColor = <string>shadowColor.color
    this.shadowOpacity = shadowColor.opacity
  }

  protected applyProps(): Promise<void> {
    return new Promise((resolve, reject) => {
      const handler = async () => {
        const registeredFontFamily = this.fallbackFont ?
          getRegisterFamilyName(this.fontFamily, 400, false) :
          getRegisterFamilyName(this.fontFamily, this.width, this.italic)

        await super.applyProps()
        this.object.set({
          width: this.areaWidth,
          height: this.areaHeight,
        })
        this.textObject.set({
          text: this.text,
          width: this.areaWidth,
          height: this.areaHeight,
          fill: (typeof this.color === 'string') ?
            this.color :
            makeFabricGradient(this.object.width ?? 48, this.object.height ?? 48, this.color),
          shadow: makeShadow(this),
          underline: this.underline,
          linethrough: this.linethrough,
          textAlign: this.round !== 0 ? TextAlign.Left : this.align,
          lineHeight: this.lineSpacing / this.fontSize,
          fontSize: this.fontSize,
          fontFamily: registeredFontFamily,
          fontStyle: this.fallbackFont && this.italic ? 'italic' : 'normal',
          fontWeight: this.fallbackFont ? this.width : 400,
          flipX: this.object.flipX,
          flipY: this.object.flipY,
        })

        if (this.dirtyPosition || this.dirtyFont) {
          this.setTextPosition()
        }

        this.dirtyFont = false
        this.dirtyPosition = false
        this.object.setCoords()
      }

      if (this.dirtyFont) {
        loadFonts([ {
          italic: this.italic,
          width: this.width,
          fontFamily: this.fontFamily,
        } ])
          .then((fallbackUsed) => {
            this.fallbackFont = fallbackUsed
            handler().then(() => {
              resolve()
            })
          })
          .catch((error) => {
            handler().then(() => {
              console.log(error)
              reject('Не удалось загрузить шрифт')
            })
          })
      } else {
        handler().then(() => {
          resolve()
        })
      }
    })
  }

  lastHeight = 0
  lastWidth = 0
  lastGroupScale = 1

  onSelect(): void {
    super.onSelect()
    this.lastWidth = this.object.getScaledWidth()
    this.lastHeight = this.object.getScaledHeight()
    this.lastGroupScale = 1
  }

  onUnselect(): void {
    if (
      TextState.Edit in this.states &&
      this.object.canvas?.getActiveObject() === (this.states[TextState.Edit] as EditTextState).editor
    ) {
      return
    }
    super.onUnselect()
  }

  onScaling(): boolean {
    this.textObject.set({
      flipX: this.object.flipX,
      flipY: this.object.flipY,
    })
    if (this.object.group) {
      const scaleX = this.object.group.scaleX ?? 1
      const scaleY = this.object.group.scaleY ?? 1
      this.object.set({
        scaleX: 1 / scaleX,
        scaleY: 1 / scaleY,
      })
      if (scaleX === scaleY) {
        this.textObject.set({ fontSize: this.fontSize * scaleX })
        this.lastGroupScale = scaleX
      }
      if (scaleX !== 1) {
        this.areaWidth = this.lastWidth * scaleX
        this.object.set({ width: this.areaWidth })
        this.setTextPosition()
      }
    } else if (this.object.scaleY && this.object.scaleY !== 1) {
      this.areaWidth = (this.object.width ?? 0) * this.object.scaleY
      this.areaHeight = (this.object.height ?? 0) * this.object.scaleY
      this.fontSize = this.fontSize * this.object.scaleY
      this.lineSpacing = this.lineSpacing * this.object.scaleY
  
      this.object.set({
        scaleX: 1,
        scaleY: 1,
        width: this.areaWidth,
        height: this.areaHeight,
      })
      this.textObject.set({
        fontSize: this.fontSize,
        lineHeight: this.lineSpacing / this.fontSize,
      })
      this.setTextPosition()
    } else if (this.object.scaleX && this.object.scaleX !== 1) {
      this.areaWidth = (this.object.width ?? 0) * this.object.scaleX
  
      this.object.set({
        scaleX: 1,
        width: this.areaWidth,
      })
      this.setTextPosition()
    }

    this.updateGradientPoints()

    return true
  }

  afterScaling(): boolean {
    if (this.object.group !== undefined) {
      this.fontSize *= this.lastGroupScale
      this.lineSpacing *= this.lastGroupScale
      this.object.set({
        scaleX: 1 / this.lastGroupScale,
        scaleY: 1 / this.lastGroupScale,
        width: this.areaWidth,
        height: this.areaHeight,
      })
      this.textObject.set({
        fontSize: this.fontSize,
        lineHeight: this.lineSpacing / this.fontSize,
      })
      return true
    }
    return false
  }

  getCanvasManagedProperties(): ActualVersion {
    return Object.assign(
      super.getCanvasManagedProperties(),
      {
        areaWidth: this.areaWidth,
        areaHeight: this.areaHeight,
        fontSize: this.fontSize,
        lineSpacing: this.lineSpacing,
      },
    ) as ActualVersion
  }

  onDoubleClick(): void {
    this.addState(TextState.Edit, new EditTextState())
  }

  toObject(): object {
    const data = super.toObject()
    data.version = ActualVersionNumber

    if ('textData' in data) {
      delete data.textData
    }
    if ('textObject' in data) {
      delete data.textObject
    }
    if ('lastHeight' in data) {
      delete data.lastHeight
    }
    if ('lastWidth' in data) {
      delete data.lastWidth
    }
    if ('lastGroupScaleX' in data) {
      delete data.lastGroupScaleX
    }
    if ('roundPath' in data) {
      delete data.roundPath
    }
    if ('dirtyFont' in data) {
      delete data.dirtyFont
    }
    if ('dirtyPosition' in data) {
      delete data.dirtyPosition
    }

    return data
  }

  setControls() {
    if (!this.object.group) {
      setTextboxSideControls(this.object, this.round === 0)
    } else {
      super.setControls()
    }
  }

  setTextPosition() {
    if (this.round === 0) {
      this.textObject.set({
        left: -(this.object.width ?? 0) / 2,
        top: -(this.textObject.height ?? 0) / 2,
      })
      this.textObject.set({
        width: this.areaWidth,
        path: undefined,
      })

      if (!this.object.group) {
        if (this.textObject.width && this.textObject.width > this.areaWidth) {
          this.areaWidth = this.textObject.width
          this.object.set({
            left: this.left,
            top: this.top,
          })
        } else {
          this.left = this.object.left ?? this.left
          this.top = this.object.top ?? this.top
        }
      }

      this.object.set({
        width: this.areaWidth,
        height: this.areaHeight,
      })
      this.textObject.set({
        left: -(this.object.width ?? 0) / 2,
        top: -(this.textObject.height ?? 0) / 2,
      })

      this.textObject.set({ text: this.text })
      this.areaWidth = this.getRealObjectWidth()

      this.areaHeight = this.textObject.height ?? this.areaHeight

      this.object.set({
        height: this.areaHeight,
      })
    } else {
      const textInOneLine = this.text.replaceAll('\n', '')
      if (this.textObject.textLines.length > 1) {
        this.textObject.set({ text: textInOneLine })
        this.bringToOneLine()
      } else if ((this.textObject.width ?? 0) > (this.textObject.dynamicMinWidth ?? 0)) {
        this.textObject.set({ width: new fabric.FabricText(textInOneLine, {
          width: this.areaWidth,
          height: this.areaHeight,
          lineHeight: this.lineSpacing / this.fontSize,
          fontSize: this.fontSize,
          fontFamily: this.textObject.fontFamily,
        }).width })
        this.bringToOneLine()
      }
      const width = this.textObject.width ?? 0
      const isInverse = this.round < 0
      const radius = getRoundRadius(this.round, this.fontSize, isInverse)
      const halfCircumference = Math.PI * radius
      const isConvex = width > halfCircumference
      const isRound = width > halfCircumference * 2
      const chordAngleRadians = width / (2 * radius)
      const box = getTextboxSize(radius, isInverse, isConvex, isRound, chordAngleRadians, this.fontSize)
      this.textObject.set({
        text: textInOneLine,
        path: getRoundPath(radius, isInverse, isConvex, isRound, chordAngleRadians),
        width: width,
      })
      this.object.set({
        height: box.height,
        width: box.width,
      })
      if (isRound) {
        this.textObject.setPositionByOrigin(new fabric.Point(0, 0), 'center', 'center')
      } else if (isInverse) {
        const bottomLetterPadding = this.fontSize * 0.22
        const roundedPercent = -(isConvex ? 0 : (width - (halfCircumference)) / (halfCircumference))
        this.textObject.setPositionByOrigin(
          new fabric.Point(0, bottomLetterPadding * roundedPercent),
          'center',
          'center',
        )
      } else {
        const bottomLetterPadding = this.fontSize * 0.22
        const roundedPercent = 1 - (isConvex ? (width - halfCircumference) / halfCircumference : 0)
        this.textObject.setPositionByOrigin(
          new fabric.Point(0, bottomLetterPadding * roundedPercent),
          'center',
          'center',
        )
      }
    }
    this.object.setCoords()
    this.setControls()
  }

  bringToOneLine() {
    if (this.textObject.textLines.length > 1) {
      const text = new fabric.FabricText(this.text.replaceAll('\n', ''), {
        lineHeight: this.lineSpacing / this.fontSize,
        fontSize: this.fontSize,
        fontFamily: this.textObject.fontFamily,
        fontWeight: this.width,
      })
      text.dirty = true
      this.textObject.set({ width: Math.ceil((text.width ?? 0) + 1) })
    }
  }

  updateGradientPoints() {
    this.textObject.set({
      fill: (typeof this.color === 'string') ?
        this.color :
        makeFabricGradient(this.object.width ?? 48, this.object.height ?? 48, this.color),
    })
  }

  addState(key: string, state: ObjectState): void {
    if (key === TextState.Round && key in this.states) {
      this.states[key].onRemove(this)
      this.states[key] = state
      this.states[key].onAdd(this)
    } else {
      super.addState(key, state)
    }
  }

  get name(): string {
    return this.text
  }

  get bodyColour(): Rgba {
    if (typeof this.color === 'string') {
      return parseRgba(this.color)
    }
    if (this.color.colorStops === undefined) {
      return { r: 0, g: 0, b: 0, a: 1 }
    }
    let prevOffset = 0
    let maxSize = 0
    let maxColour = 'rgba(0,0,0,1)'
    for (const colorStop of this.color.colorStops.sort((a, b) => { return a.offset - b.offset })) {
      const size = colorStop.offset - prevOffset
      if (size > maxSize) {
        maxSize = size
        maxColour = colorStop.color
      }
      prevOffset = colorStop.offset
    }
    return parseRgba(maxColour)
  }
}

export class EditTextState extends ObjectState {
  editor: fabricTypes.Textbox | undefined
  oldData: ActualVersion = {}

  onAdd(object: Text): Promise<void> {
    this.oldData = {
      text: object.text,
      areaWidth: object.areaWidth,
      areaHeight: object.areaHeight,
      left: object.left,
      top: object.top,
    }
    if (window && window.innerWidth < DESKTOP_WIDTH) {
      useNuxtApp().$event('text:open-editor', {
        initialText: object.text,
        callback: (text: string) => {
          const oldData: ActualVersion = {
            text: object.text,
            areaWidth: object.areaWidth,
            areaHeight: object.areaHeight,
            left: object.left,
            top: object.top,
          }
          object.removeState(TextState.Edit)
          object.text = text
          object.textObject.set('text', object.text)
          object.areaWidth = Math.max(object.areaWidth, object.textObject.width ?? 0)
          object.areaHeight = Math.max(object.areaHeight, object.textObject.height ?? 0)
          object.setTextPosition()
          object.updateGradientPoints()
          object.object.canvas?.renderAll()
          object.object.canvas?.fire('object:modified', {
            action: Action.EditText,
            target: object.object,
            modification: [ {
              id: object.id,
              oldProps: oldData,
              newProps: {
                text: object.text,
                areaWidth: object.areaWidth,
                areaHeight: object.areaHeight,
                left: object.left,
                top: object.top,
              },
            } ],
          })
        },
      })
      return Promise.resolve()
    }
    this.editor = new fabric.Textbox(object.text, Object.assign({}, {
      width: object.areaWidth,
      height: object.areaHeight,
      fill: (typeof object.color === 'string') ?
        object.color :
        makeFabricGradient(object.object.width ?? 48, object.object.height ?? 48, object.color),
      shadow: object.shadow ?
        new fabric.Shadow({
          blur: object.shadowBlur,
          offsetX: object.shadowX,
          offsetY: object.shadowY,
          color: object.shadowColor,
        }) :
        undefined,
      underline: object.underline,
      linethrough: object.linethrough,
      textAlign: object.align,
      lineHeight: object.lineSpacing / object.fontSize,
      fontSize: object.fontSize,
      fontFamily: getRegisterFamilyName(object.fontFamily, object.width, object.italic),
    }, {
      left: (object.object.left ?? 0),
      top: (object.object.top ?? 0),
      angle: object.object.angle,
      width: object.object.width,
    }))
    object.object.set({
      opacity: object.round !== 0 ? 0.5 : 0,
    })
    object.object.canvas?.add(this.editor)
    object.object.canvas?.setActiveObject(this.editor)
    this.editor.enterEditing()
    object.object.canvas?.renderAll()
    this.editor.on('deselected', () => {
      object.removeState(TextState.Edit)
    })
    this.editor.on('changed', () => {
      object.text = this.editor?.get('text') ?? object.text
      object.textObject.set('text', object.text)
      object.setTextPosition()
      object.updateGradientPoints()
    })
    return Promise.resolve()
  }

  onRemove(object: Text): Promise<void> {
    if (!this.editor) {
      return Promise.resolve()
    }

    object.text = this.editor.get('text') ?? object.text
    object.areaWidth = this.editor.getScaledWidth()
    object.areaHeight = this.editor.getScaledHeight()
    object.textObject.set('text', object.text)
    object.object.set({
      opacity: 1,
      height: object.textObject.height,
    })
    object.setTextPosition()
    object.updateGradientPoints()
    object.object.canvas?.remove(this.editor)
    if (object.object.canvas && object.object.canvas.getActiveObjects().length === 0) {
      object.object.canvas?.setActiveObject(object.object)
    }
    object.object.canvas?.renderAll()
    this.editor = undefined
    object.object.canvas?.fire('object:modified', {
      action: Action.EditText,
      target: object.object,
      modification: [ {
        id: object.id,
        oldProps: this.oldData,
        newProps: {
          text: object.text,
          areaWidth: object.areaWidth,
          areaHeight: object.areaHeight,
          left: object.left,
          top: object.top,
        },
      } ],
    })
    return Promise.resolve()
  }
}

export class RoundTextState extends ObjectState {
  roundPath: fabricTypes.Circle | undefined

  onAdd(object: Text): Promise<void> {
    return new Promise((resolve) => {
      if (this.roundPath !== undefined) {
        object.object.canvas?.remove(this.roundPath)
      }
      if (object.round === 0) {
        object.object.canvas?.renderAll()
        this.roundPath = undefined
        return
      }
      const isInverse = object.round < 0
      const radius = getRoundRadius(object.round, object.fontSize, isInverse)
      const letterHeight = object.fontSize * 0.78
      const bottomLetterPadding = object.fontSize * 0.22

      const angleRadians = fabric.util.degreesToRadians(object.object.angle ?? 0)
      const rotateOrigin = object.object.getCenterPoint()
      const originalGroupPosition = object.object.getPointByOrigin('left', 'top').rotate(-angleRadians, rotateOrigin)
      const roundPathPosition = new fabric.Point(
        originalGroupPosition.x + (object.object.width ?? 0) / 2 - radius,
        originalGroupPosition.y +
          (isInverse ? (-radius * 2 + (object.object.height ?? 0) - bottomLetterPadding) : letterHeight),
      )
      const rotatedRoundPathPosition = roundPathPosition.rotate(angleRadians, rotateOrigin)
      this.roundPath = new fabric.Circle({
        radius: radius,
        left: rotatedRoundPathPosition.x,
        top: rotatedRoundPathPosition.y,
        strokeWidth: 3,
        stroke: '#6610F2',
        fill: undefined,
        angle: object.object.angle,
      })
      object.object.setCoords()
      object.object.canvas?.add(this.roundPath)
      object.object.canvas?.renderAll()
      object.setControls()
      resolve()
    })
  }

  onRemove(object: Text): Promise<void> {
    return new Promise((resolve) => {
      if (this.roundPath !== undefined) {
        object.object.canvas?.remove(this.roundPath)
        object.object.canvas?.renderAll()
        this.roundPath = undefined
      }
      resolve()
    })
  }
}
